import { useAuth } from "@clerk/clerk-react";
import { STAFF_TENANT_CHANGE_EVENT, migrateLegacyStaffTenantPrefs } from "@l30/shared";
import { ExtractionReviewHost } from "@l30/web-atlas/ExtractionReviewProvider";
import { AtlasHubProvider } from "@l30/web-atlas/host";
import { BoardHubProvider } from "@l30/web-board/host";
import { DocumentsHubProvider } from "@l30/web-documents/host";
import { ProgrammeHubProvider } from "@l30/web-programme/host";
import { createRoute, Outlet } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useMemo } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";

import { createAtlasHubServices } from "../atlas/createAtlasHubServices.js";
import { createBoardHubServices } from "../board/createBoardHubServices.js";
import { CaptureProvider } from "../components/capture/capture-provider.js";
import { CommandPaletteProvider } from "../components/command-palette/command-palette.js";
import { HelpProvider } from "../components/help/help-context.js";
import { Header } from "../components/layout/header.js";
import { SHOW_LEFT_NAV } from "../components/layout/shell-flags.js";
import { Sidebar } from "../components/layout/sidebar.js";
import { StaffRail } from "../components/layout/staff-rail.js";
import { ErrorState } from "../components/ui/error-state.js";
import { Spinner } from "../components/ui/spinner.js";
import { ThemeProvider } from "../design/theme-provider.js";
import { createDocumentsHubServices } from "../documents/createDocumentsHubServices.js";
import { isLocalLabAuthMode } from "../lib/auth-mode.js";
import { queryClient } from "../lib/query-client.js";
import { createProgrammeHubServices } from "../programme/createProgrammeHubServices.js";

import { rootRoute } from "./__root.js";

// Lazy: keep Atlas extraction watcher off the hub entry graph (ADR-0101 Phase 2).
const ExtractionStatusToasts = lazy(() =>
  import("@l30/web-atlas/ExtractionStatusToasts").then((m) => ({
    default: m.ExtractionStatusToasts,
  })),
);

export const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  if (isLocalLabAuthMode) {
    return <AuthenticatedShell />;
  }

  return <ClerkAuthenticatedLayout />;
}

function ClerkAuthenticatedLayout() {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner className="h-6 w-6" label="Loading your workspace…" />
      </div>
    );
  }

  if (!isSignedIn) {
    // Preserve the intended destination so Clerk can return after sign-in.
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    // eslint-disable-next-line l30/no-window-location, react-hooks/immutability -- pre-Clerk redirect to /sign-in; we don't have router context yet, so window.location is the only path (canon 01-ui/navigation.md exception)
    window.location.href = `/sign-in?return_to=${returnTo}`;
    return null;
  }

  return <AuthenticatedShell />;
}

function AuthenticatedShell() {
  const atlasHubServices = useMemo(() => createAtlasHubServices(), []);
  const documentsHubServices = useMemo(() => createDocumentsHubServices(), []);
  const programmeHubServices = useMemo(() => createProgrammeHubServices(), []);
  const boardHubServices = useMemo(() => createBoardHubServices(), []);

  useEffect(() => {
    migrateLegacyStaffTenantPrefs();
    const refresh = () => {
      queryClient.clear();
    };
    window.addEventListener(STAFF_TENANT_CHANGE_EVENT, refresh);
    return () => window.removeEventListener(STAFF_TENANT_CHANGE_EVENT, refresh);
  }, []);

  return (
    // ThemeProvider mounted at the authenticated root. Currently no
    // tenant theme is fetched; default theme (from globals.css)
    // applies. When `useTenantConfig()` lands (commercial-readiness
    // backlog), pass `theme={tenantConfig.theme}` here. Canon:
    // docs/design-canon/08-design/theming.md R1.
    <ThemeProvider>
      <AtlasHubProvider value={atlasHubServices}>
        <DocumentsHubProvider value={documentsHubServices}>
          <ProgrammeHubProvider value={programmeHubServices}>
            <BoardHubProvider value={boardHubServices}>
              <HelpProvider>
                <CaptureProvider>
                  <CommandPaletteProvider>
                    <ExtractionReviewHost>
                      <Suspense fallback={null}>
                        <ExtractionStatusToasts />
                      </Suspense>
                      <div className="flex h-screen flex-col md:flex-row">
                        {SHOW_LEFT_NAV ? <Sidebar /> : <StaffRail />}
                        <div className="flex flex-1 flex-col overflow-hidden">
                          <Header />
                          {/* ErrorBoundary catches render-time errors that escape
                        useQuery's error handling. Per canon
                        01-ui/feedback-states.md R7. */}
                          <main className="flex-1 overflow-auto bg-surface-muted p-3 md:p-6">
                            <ErrorBoundary FallbackComponent={LayoutErrorFallback}>
                              <Outlet />
                            </ErrorBoundary>
                          </main>
                        </div>
                      </div>
                    </ExtractionReviewHost>
                  </CommandPaletteProvider>
                </CaptureProvider>
              </HelpProvider>
            </BoardHubProvider>
          </ProgrammeHubProvider>
        </DocumentsHubProvider>
      </AtlasHubProvider>
    </ThemeProvider>
  );
}

function LayoutErrorFallback({ error }: FallbackProps) {
  // The error is whatever escaped the route — could be a render
  // exception, a thrown AppError-like object, or a runtime TypeError.
  // We don't try to extract code/requestId here because the fallback
  // is for things that broke OUTSIDE the API-error pipeline; specific
  // API errors render their own ErrorState in-route.
  const message = error instanceof Error ? error.message : "An unexpected error occurred.";
  return (
    <div className="mx-auto max-w-2xl">
      <ErrorState
        title="Something went wrong on this page"
        message={message}
        retry={() => window.location.reload()}
      />
    </div>
  );
}
